import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Heart } from 'lucide-react';
import { ClearSunIcon, ParallelBarsIcon, CrescentMoonIcon, SlashCircleIcon } from '../state/StateIcons';
import { IRIS_PURPLE, LIVING_CORAL, SHELL_WHITE, TWILIGHT_NAVY, alpha } from '../../theme/colors';

const MAIN_FADE_DURATION = 1.0; // seconds — the whole card, tags included, fades/slides in as one unit
const HEART_POP_DELAY = 0.3; // pause after the card finishes fading in, before any tag hearts
const HEART_POP_STAGGER = 0.25; // seconds between each hearted tag's own pop
const HEART_POP_DURATION = 0.4; // visual length of one heart's pop keyframe

// Desktop-only decorative mockup of the real MoodBoard (src/components/MoodBoard.js)
// for the Hero. Not wired to a user's real check-in — sample content only.
// Shows today's actual date so it doesn't look stale/canned on repeat visits.

const TAGS = [
  { text: 'lower the lights an hour earlier tonight', hearted: false },
  { text: 'let this week be gentler than last', hearted: true },
  { text: 'one slow exhale before you open your phone', hearted: true },
  { text: 'text someone who makes you laugh easily', hearted: false },
];
const HEARTED_COUNT = TAGS.filter((t) => t.hearted).length;

// Exported so Hero.js can delay the DotCalendar cluster until this card is
// fully done animating (card fade-in + every tag's heart pop), rather than
// the two animating simultaneously.
export const MOOD_BOARD_SEQUENCE_DURATION =
  MAIN_FADE_DURATION + HEART_POP_DELAY + Math.max(HEARTED_COUNT - 1, 0) * HEART_POP_STAGGER + HEART_POP_DURATION;

const CornerSignal = ({ Icon, label, value, align, labelPosition }) => {
  const isRight = align === 'right';
  const labelEl = (
    <span style={{ fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.08em', color: SHELL_WHITE, lineHeight: 1 }}>
      {label}
    </span>
  );
  const valueRow = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexDirection: isRight ? 'row-reverse' : 'row' }}>
      <Icon size={14} color={SHELL_WHITE} />
      <span style={{ fontSize: 13, fontWeight: 500, color: SHELL_WHITE, lineHeight: 1.2, whiteSpace: 'nowrap' }}>{value}</span>
    </div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: isRight ? 'flex-end' : 'flex-start' }}>
      {labelPosition === 'top' ? labelEl : valueRow}
      {labelPosition === 'top' ? valueRow : labelEl}
    </div>
  );
};

// Renders in place as part of the card's single fade-in — no entrance
// animation of its own. Tags that are actually "hearted" start out looking
// unhearted, then flip to hearted (background/border cross-fade + a quick
// spring pop on the heart icon itself) after the card has finished fading
// in, like the tag was just tapped. `heartDelay` is only meaningful when
// `hearted` is true.
const TagPill = ({ text, hearted, heartDelay, started }) => {
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (!started || !hearted) return;
    const timer = setTimeout(() => setRevealed(true), (MAIN_FADE_DURATION + HEART_POP_DELAY + heartDelay) * 1000);
    return () => clearTimeout(timer);
  }, [started, hearted, heartDelay]);

  const active = hearted && revealed;

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '9px 14px',
        borderRadius: 20,
        background: active ? alpha('beige', 0.92) : alpha('white', 0.5),
        border: `1.5px solid ${active ? IRIS_PURPLE : alpha('white', 0.7)}`,
        boxShadow: active ? `0 2px 12px ${alpha('purple', 0.2)}` : 'none',
        transition: 'background 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',
      }}
    >
      <motion.span
        style={{ display: 'flex', flexShrink: 0 }}
        animate={active ? { scale: [1, 1.4, 1] } : { scale: 1 }}
        transition={{ duration: HEART_POP_DURATION, times: [0, 0.5, 1] }}
      >
        <Heart size={11} color={LIVING_CORAL} fill={active ? LIVING_CORAL : 'none'} style={{ opacity: active ? 1 : 0.5 }} />
      </motion.span>
      <span style={{ fontFamily: 'DM Sans', fontSize: 13, color: TWILIGHT_NAVY }}>{text}</span>
    </div>
  );
};

const HeroMoodBoardMock = ({ style }) => {
  const dateLabel = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  const ref = useRef(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStarted(true);
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        position: 'relative',
        width: 380,
        borderRadius: 16,
        overflow: 'hidden',
        border: '0.5px solid #FFFFFF',
        boxShadow: `0 12px 36px ${alpha('navy', 0.22)}`,
        opacity: started ? 1 : 0,
        transform: started ? 'translateY(0)' : 'translateY(16px)',
        transition: `opacity ${MAIN_FADE_DURATION}s ease, transform ${MAIN_FADE_DURATION}s ease`,
        ...style,
      }}
    >
      <img
        src="/state-assets/backgrounds/e4s2-bg.svg"
        alt=""
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
      />
      <div style={{ position: 'relative' }}>
        <div
          style={{
            position: 'relative',
            minHeight: 200,
            padding: '20px 24px 28px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            borderBottom: `0.5px solid ${alpha('white', 0.4)}`,
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ fontFamily: 'Instrument Serif', fontSize: 28, marginBottom: 3, color: SHELL_WHITE }}>Mood Board</div>
            <div style={{ fontFamily: 'DM Sans', fontSize: 10, fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: SHELL_WHITE, opacity: 0.7 }}>
              {dateLabel}
            </div>
          </div>

          <div style={{ position: 'absolute', top: 88, left: 16 }}>
            <CornerSignal Icon={ClearSunIcon} label="Energy" value="Energized" align="left" labelPosition="top" />
          </div>
          <div style={{ position: 'absolute', top: 88, right: 16 }}>
            <CornerSignal Icon={ParallelBarsIcon} label="Strain" value="Mild" align="right" labelPosition="top" />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <img
              src="/state-assets/icons/e4s2-icon.svg"
              alt=""
              style={{ width: 80, height: 80, filter: `drop-shadow(0 4px 16px ${alpha('purple', 0.25)})` }}
            />
            <span style={{ fontFamily: 'DM Sans', fontSize: 11, fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase', color: SHELL_WHITE }}>
              Channel, don&apos;t stack
            </span>
          </div>

          <div style={{ position: 'absolute', bottom: 20, left: 16 }}>
            <CornerSignal Icon={CrescentMoonIcon} label="Sleep" value="Disrupted" align="left" labelPosition="bottom" />
          </div>
          <div style={{ position: 'absolute', bottom: 20, right: 16 }}>
            <CornerSignal Icon={SlashCircleIcon} label="Mental load" value="Busy" align="right" labelPosition="bottom" />
          </div>
        </div>

        <div style={{ padding: '14px 16px', display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
          {(() => {
            let heartedSoFar = 0;
            return TAGS.map((tag) => {
              const heartDelay = tag.hearted ? heartedSoFar++ * HEART_POP_STAGGER : 0;
              return <TagPill key={tag.text} text={tag.text} hearted={tag.hearted} heartDelay={heartDelay} started={started} />;
            });
          })()}
        </div>
      </div>
    </div>
  );
};

export default HeroMoodBoardMock;
